<template>
  <div id="_tlqn_member_questionnaire_contents" class="column no-wrap">
    <header
      :style="{
        height: `${Math.min(160, Math.max(50, dynamicHeaderHeight))}px`,
        fontSize: `${(Math.min(100, Math.max(65, dynamicHeaderHeight)) * 2) /
          100}rem`,
      }"
      class="column justify-end"
    >
      <HamburgerBtn
        :style="{
          color: '#fff',
          top: '10px',
          right: '10px',
          position: 'absolute',
        }"
        v-model="showQuestionnaireContents"
      />
      <span class="text-bold">问卷目录</span>
    </header>
    <q-scroll-area
      class="column col-grow q-px-sm q-pr-lg"
      @scroll="handleScroll"
    >
      <Loading
        class="q-mt-xl"
        v-if="member_status_store[LOADING_QUESTIONNAIRE_CONTENTS]"
      />
      <Tree
        v-else
        :nodes="contents"
        label-key="leveled_name"
        :default-expand-all="true"
        v-model="currentQuestionGroup"
        :validatedAnswerCache="validatedAnswerCache"
      />
      <div class="q-py-xl full-width"></div>
    </q-scroll-area>
  </div>
</template>

<script>
import QuestionnaireMixin from "./questionnaire.mixin";
import { styleLog } from "../../../utils/style-log";
import { getQuestionGroupList } from "@/api/endpoints/question-group/getQuestionGroupList";
import { getQuestionGroupIdsNotEmpty } from "@/api/endpoints/question-group/getQuestionGroupIdsNotEmpty";
import { roughlyToPlain } from "../../../utils/roughly-to-plain";
import MemberStatusMixin from "../mixins/status.mixin";
import { LOADING_QUESTIONNAIRE_CONTENTS } from "@/status/member.status";
import LocalForageManager from "@/libs/LocalForageManager";
const nativeQuesCacheManger = new LocalForageManager(
  "__questionnaire_contents__"
);

export default {
  props: {
    version: {
      type: String,
      default: "",
    },
  },
  components: {
    HamburgerBtn: () => import("@/components/HamburgerBtn"),
    Loading: () => import("@/components/Loading"),
    Tree: () => import("./Tree"),
  },
  mixins: [QuestionnaireMixin, MemberStatusMixin],
  data() {
    return {
      dynamicHeaderHeight: 160,
      expandedNodes: [],
      selectedUnit: null,
      LOADING_QUESTIONNAIRE_CONTENTS,
    };
  },

  mounted() {
    this.loadContents();
  },

  methods: {
    handleScroll(prop) {
      const { verticalPercentage } = prop;
      this.dynamicHeaderHeight = 160 - verticalPercentage * 200;
    },
    // 加载目录
    async loadContents() {
      this.setCommonStatus(LOADING_QUESTIONNAIRE_CONTENTS, true);
      const contentsLocal = await nativeQuesCacheManger.get("_contents");
      const notEmpLocal = await nativeQuesCacheManger.get("_non_emp_gps");
      const localVersition = await nativeQuesCacheManger.get("_version");

      const invalid =
        !contentsLocal || !notEmpLocal || localVersition !== this.version;

      if (invalid) {
        try {
          const [qgListResponse, qgIdsResponse] = await Promise.all([
            getQuestionGroupList("tree"),
            getQuestionGroupIdsNotEmpty(),
          ]);
          if (!qgListResponse || qgListResponse.status) {
            styleLog.dangerPrefix(qgListResponse.detail);
            this.$q.notify({ type: "danger", message: qgListResponse.detail });
          } else if (!qgIdsResponse || qgIdsResponse.status) {
            styleLog.dangerPrefix(qgIdsResponse.detail);
            this.$q.notify({ type: "danger", message: qgIdsResponse.detail });
          } else {
            const contents = this.prevResolveTree(
              qgListResponse,
              qgIdsResponse
            );
            const questionGroupNotEmpty = qgIdsResponse;

            await nativeQuesCacheManger.set("_contents", contents);
            await nativeQuesCacheManger.set(
              "_non_emp_gps",
              questionGroupNotEmpty
            );
            await nativeQuesCacheManger.set("_version", this.version);

            this.contents = contents;
            this.questionGroupNotEmpty = questionGroupNotEmpty;
            this.init();
          }
        } catch (err) {
          styleLog.dangerPrefix(err);
          this.$q.notify({
            type: "danger",
            message: "加载目录出现未知错误",
          });
        }
      } else {
        this.contents = contentsLocal;
        this.questionGroupNotEmpty = notEmpLocal;
        this.init();
      }

      this.setCommonStatus(LOADING_QUESTIONNAIRE_CONTENTS, false);
    },

    /**
     * 对树结构进行预处理
     * 1. 添加标号
     * 2. 增加 parent 字段
     * 3. 默认展开树节点
     * @param {Object|Array} tree
     * @param {string[]} notEmptyIds
     */
    prevResolveTree(tree, notEmptyIds) {
      tree = roughlyToPlain(tree);
      const level1 = [];
      for (let i = "A".charCodeAt(); i <= "Z".charCodeAt(); i++)
        level1.push(String.fromCharCode(i));

      const level2 = [
        "一",
        "二",
        "三",
        "四",
        "五",
        "六",
        "七",
        "八",
        "九",
        "十",
      ];
      const level3 = level2.map((l) => `（${l}）`);
      const levels = [level1, level2, level3];
      const splits = [".", "、", ""];
      const dig = (node, parent = null, level = 0, index = 0) => {
        if (Object.prototype.toString.call(node) === "[object Array]") {
          node.forEach((_node, i) => dig(_node, parent, level, i));
        } else {
          if (!levels[level] || !levels[level][index])
            styleLog.dangerPrefix(`Cannot find levels[${level}][${index}]`);
          node.leveled_name = `${levels[level][index]}${splits[level] || ""}${
            node.name
          }`;
          node.parent = parent;
          if (notEmptyIds.indexOf(node.id) < 0) node.empty = true;
          if (node.children) {
            this.expandedNodes.push(node.id);
            dig(node.children, node.id, level + 1);
          }
        }
      };
      dig(tree);
      return tree;
    },

    /**
     * 初始化
     */
    init() {
      this.currentQuestionGroup = this.nextQuestionGroup;
    },
  },
};
</script>

<style lang="scss">
@import "@/styles/quasar";
#_tlqn_member_questionnaire_contents {
  box-shadow: 10px 0px 10px rgba(0, 0, 0, 0.1);

  header {
    color: #fff;
    background-color: $secondary;
    position: relative;
    padding: 0 0 10px 40px;
    &::before,
    &::after {
      position: absolute;
      content: "";
      width: 10px;
      left: 0;
    }

    &::before {
      background-color: $primary;
      height: 40%;
      top: 0;
      z-index: 1;
      min-height: 50px;
    }
    &::after {
      background-color: $tailong-blue;
      height: 60%;
      bottom: 0;
    }
  }

  .q-scroll {
    flex-grow: 1;
  }

  .q-tree__node--selected {
    background-color: $primary;
    font-weight: bold;
  }
}
</style>
